/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import CampaignModel from 'cio/models/campaign';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';
import DeviceAttributeCondition, { SpecialDeviceAttributes } from 'cio/app-models/conditions/device-attribute';
import { findOperator } from 'cio/utils/conditions-operators';
import { isNone } from '@ember/utils';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';

interface EditorArgs {
  condition?: DeviceAttributeCondition;
  campaign?: CampaignModel;
  disabled?: boolean;
  attributes: Record<string, any>;
  variation?: string;
  subLayout?: ConditionEditorSubLayout;
  direction?: string;
  size?: string;
  dropdownClass?: string;
  marginClass?: string;
  allowArrayOperators?: boolean;
}

const ERROR_SOURCE_NAME_MAP: Record<string, string> = {
  'broadcasts.newsletter.setup.recipients.index': 'Newsletter Recipients Setup Page',
  'people.list.index': 'People Page',
};

export default class Editor extends Component<EditorArgs> {
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  // Prevent the same analytics event from firing when errors are re-computed
  @tracked trackedErrors: string[] = [];

  @computed('args.condition.operator')
  get operatorHasPredicate() {
    let { condition } = this.args;
    if (isNone(condition) || condition.name === SpecialDeviceAttributes.id) {
      return true;
    }
    let operatorDefinition = findOperator(condition.operator);
    return operatorDefinition?.hasPredicate;
  }

  get nonCompatibleAttributesFilter() {
    let filter = (item: { label: string; value: string }) => item.value !== SpecialDeviceAttributes._last_used;
    return filter;
  }

  @computed('args.condition.{name,operator}')
  get showValue() {
    let { condition } = this.args;
    return (condition?.isSpecialAttribute ?? false) || this.operatorHasPredicate;
  }

  @action
  trackErrorChange(_: HTMLElement, [errors]: [string[]?]) {
    if (errors != null && errors.length !== 0 && !this.trackedErrors.includes(errors[0])) {
      this.trackedErrors = [...this.trackedErrors, errors[0]];

      this.analytics.track('user', 'error_encountered', {
        currentUrl: this.router.currentURL,
        message: errors[0],
        source: ERROR_SOURCE_NAME_MAP[this.router.currentRouteName],
      });
    }
  }
}
